import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ListingCard, ListingCardProps } from './ListingCard';
import { ResultSkeleton, skeletonVariantFor } from './search/ResultSkeleton';
interface ListingCarouselProps {
  listings: ListingCardProps[];
  /**
   * Items from this index onward are still "on their way": the real card is
   * laid out (so its size is final) but hidden under a skeleton. Lowering it
   * reveals cards in place without any layout movement.
   */
  pendingFrom?: number;
}
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
export function ListingCarousel({
  listings,
  pendingFrom
}: ListingCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, {
      passive: true
    });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [listings]);
  const scrollBy = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({
      left: dir === 'left' ? -amount : amount,
      behavior: 'smooth'
    });
  };
  // Before the first scroll, the right control gets a soft ambient halo so the
  // sideways row is impossible to miss. It stops the moment the user moves.
  const loading = pendingFrom !== undefined && pendingFrom < listings.length;
  const showHint = !loading && !canScrollLeft && canScrollRight;
  const controlBase = 'hidden md:flex absolute top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-primary-indigo text-white ring-2 ring-white shadow-large hover:bg-indigo-700 hover:scale-105 active:scale-95 transition-[background-color,transform,opacity] duration-150 ease-out';
  return <div className="relative group/carousel" data-id="element-2599" data-mp="25jq9dq9p9x33">
      {/* Edge fades — soft signal that the row continues sideways */}
      <div className={`hidden md:block pointer-events-none absolute left-0 top-0 bottom-4 w-20 z-10 bg-gradient-to-r from-white via-white/80 to-transparent transition-opacity duration-200 ease-out ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`} data-id="element-2600" data-mp="20los9avu47u9" />
      <div className={`hidden md:block pointer-events-none absolute right-0 top-0 bottom-4 w-20 z-10 bg-gradient-to-l from-white via-white/80 to-transparent transition-opacity duration-200 ease-out ${canScrollRight ? 'opacity-100' : 'opacity-0'}`} data-id="element-2601" data-mp="29nxz9qrw9j2w" />

      {/* Left Control */}
      <button onClick={() => scrollBy('left')} aria-label="Previous listings" className={`${controlBase} -left-3 ${canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-2602" data-mp="3tjvby5hyflxi">
        <ChevronLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2603" data-mp="3oio8gimuh8wn" />
      </button>

      {/* Right Control */}
      <div className={`hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 transition-opacity duration-150 ease-out ${canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-2604" data-mp="g34hxrdk64la">
        {showHint && <motion.span aria-hidden="true" className="absolute inset-0 rounded-full bg-primary-indigo/30" animate={{
        scale: [1, 1.5],
        opacity: [0.5, 0]
      }} transition={{
        duration: 1.8,
        repeat: Infinity,
        ease: 'easeOut'
      }} data-id="element-2605" data-mp="6cjki79mf63y" />}
        <button onClick={() => scrollBy('right')} aria-label="Next listings" className="relative w-11 h-11 flex items-center justify-center rounded-full bg-primary-indigo text-white ring-2 ring-white shadow-large hover:bg-indigo-700 hover:scale-105 active:scale-95 transition-[background-color,transform] duration-150 ease-out" data-id="element-2606" data-mp="3bctcmg9mlyik">
          <ChevronRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2607" data-mp="3v0ix02f8gwhr" />
        </button>
      </div>

      {/* Scroll Track */}
      <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-proximity no-scrollbar" data-id="element-2608" data-mp="3q1zb220n3kpe">
        {listings.map((listing, idx) => {
        const pending = pendingFrom !== undefined && idx >= pendingFrom;
        return <motion.div key={listing.id} id={`listing-${listing.id}`} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1],
          delay: Math.min(idx, 6) * 0.04
        }} className="relative snap-start flex-shrink-0 w-[250px] sm:w-[280px] rounded-2xl" data-id="element-2609" data-mp="hkur5ogrz3hr" data-mpi={idx}>
              <div aria-hidden={pending || undefined} style={{
            opacity: pending ? 0 : 1,
            visibility: pending ? 'hidden' : 'visible',
            // Arrives out of a soft focus: slight blur and scale resolve
            // into the card, like it's coming into view.
            transform: pending ? 'translateY(4px) scale(0.985)' : 'none',
            filter: pending ? 'blur(6px)' : 'blur(0px)',
            transition: `opacity 280ms ${EASE_OUT}, transform 280ms ${EASE_OUT}, filter 280ms ${EASE_OUT}, visibility 0s linear ${pending ? '280ms' : '0s'}`
          }} data-id="element-2610" data-mp="u5tcr0o8plwh">
                <ListingCard {...listing} data-id="element-2611" data-mp="1qu92oxv9hxa4" />
              </div>
              {/* A brief indigo→purple edge of light as each result lands. */}
              {pendingFrom !== undefined && !pending && <motion.span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-[1.5px] ring-ai-purple/50" initial={{
            opacity: 0.9
          }} animate={{
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1],
            delay: 0.06
          }} data-id="element-2612" data-mp="3ixde4ofmjuaz" />}
              <AnimatePresence data-id="element-2613" data-mp="5fuk8nallt2o">
                {pending && <motion.div className="absolute inset-0" initial={false} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-2614" data-mp="jg5gy6yla1vr">
                    <ResultSkeleton variant={skeletonVariantFor(listing)} data-id="element-2615" data-mp="2l81z3zf9i9zi" />
                  </motion.div>}
              </AnimatePresence>
            </motion.div>;
      })}
      </div>
    </div>;
}